import {
  ModalBg,
  ModalCloseButton,
  ModalContent,
  ModalOuter,
  portalRoot,
} from "../Modal"
import React, { useCallback, useContext, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { css } from "styled-components"
import { H2 } from "../atoms/Headings"
import { Link } from "../atoms/Link"
import { Margin } from "styled-components-spacing"
import ReactDOM from "react-dom"
import { ButtonAnchor, Button } from "../atoms/Buttons"
import { P } from "../atoms/Elements"
import { VisitorLocaleContext } from "../providers/VisitorLocaleProvider"
import { multiAddError } from "../hooks/useAddtoKarbon.tsx"

export const AddToKarbonErrorModal = props => {
  const { karbonData } = useContext(VisitorLocaleContext)
  const [modalOpen, setModalOpen] = useState()
  const hide = () => setModalOpen(false)
  useEffect(() => {
    if (props.error) {
      setModalOpen(true)
    }
  }, [props.error])

  const LoginErrorContent = useCallback(() => {
    return (
      <div
        css={css`
          text-align: center;
        `}
      >
        <H2 mt={0} centered>
          {`Login to Karbon`}
        </H2>

        <P>
          You don't seem to be logged in. You'll need to login to Karbon with an
          admin account before adding a template.
        </P>
        <Margin top={3} bottom={2}>
          <ButtonAnchor
            size={2}
            href={
              karbonData.data.dataCenterUrl ||
              "https://app2.karbonhq.com/signin"
            }
            upperCase
            target={"_blank"}
            onClick={hide}
          >
            Login
          </ButtonAnchor>
        </Margin>
      </div>
    )
  }, [props.error])

  const SingleImportErrorContent = useCallback(() => {
    return (
      <Margin
        bottom={2}
        top={2}
        css={css`
          text-align: center;
          padding: 0 24px;
        `}
      >
        <H2 centered>Unable to Add This Template to Karbon</H2>
        <div
          css={css`
            font-size: 16px;
          `}
        >
          We’re sorry, an unexpected error has occurred. The problem may be on
          our end — please try again later or{" "}
          <Link
            underlined
            as={"span"}
            id={"templates-error-bot-trigger"}
            onClick={() => {
              if (!window?.Intercom.booted) {
                window.location = "https://karbonhq.com/contact-us"
              }
            }}
          >
            contact support
          </Link>{" "}
          if the error persists.
        </div>
      </Margin>
    )
  }, [props.error])

  const MultiImportErrorContent = useCallback(() => {
    return (
      <Margin
        bottom={2}
        top={2}
        css={css`
          text-align: center;
          padding: 0 24px;
        `}
      >
        <H2 centered>Unable to Add All Templates</H2>
        {/* if all of the templates fail, then just show a generic message */}
        {props.failedTemplates.every(template => template === undefined) ? (
          <div
            css={css`
              font-size: 16px;
            `}
          >
            We’re sorry, an unexpected error has occurred. The problem may be on
            our end — please try again later or{" "}
            <Link
              underlined
              as={"span"}
              id={"templates-error-bot-trigger"}
              onClick={() => {
                if (!window?.Intercom.booted) {
                  window.location = "https://karbonhq.com/contact-us"
                }
              }}
            >
              contact support
            </Link>{" "}
            if the error persists.
          </div>
        ) : (
          <>
            <div
              css={css`
                font-size: 16px;
              `}
            >
              {/* if some templates fail, and you have the names of all of them, then display them all, otherwise change the messaging slightly */}
              {props.failedTemplates.every(template => template !== undefined)
                ? `An unexpected error has occurred for the following templates:`
                : `An unexpected error has occurred for some templates, including:`}
              <br />
              <ul>
                {props.failedTemplates.forEach(template => {
                  if (template !== undefined) {
                    return <li key={template}>{template}</li>
                  }
                })}
              </ul>
              Try again, and if the problem persists please{" "}
              <Link
                underlined
                as={"span"}
                id={"templates-error-bot-trigger"}
                onClick={() => {
                  if (!window?.Intercom.booted) {
                    window.location = "https://karbonhq.com/contact-us"
                  }
                }}
              >
                contact support
              </Link>
              .
            </div>
            <Button
              size={2}
              css={css`
                text-transform: uppercase;
                font-weight: 600;
                margin-top: 32px;
              `}
            >
              Close
            </Button>
          </>
        )}
      </Margin>
    )
  }, [props.error])

  if (modalOpen) {
    if (props.error.message === "401") {
      return ReactDOM.createPortal(
        <ModalOuter className={props.className}>
          <ModalContent>
            <LoginErrorContent />
            <ModalCloseButton onClick={hide} />
          </ModalContent>
          <ModalBg
            onClick={() => {
              hide()
            }}
          />
        </ModalOuter>,
        portalRoot
      )
    } else {
      return ReactDOM.createPortal(
        <ModalOuter className={props.className}>
          <ModalContent>
            {props.error.message === multiAddError ? (
              <MultiImportErrorContent />
            ) : (
              <SingleImportErrorContent />
            )}
            <ModalCloseButton onClick={hide} />
          </ModalContent>
          <ModalBg
            onClick={() => {
              hide()
            }}
          />
        </ModalOuter>,
        portalRoot
      )
    }
  }

  return null
}

AddToKarbonErrorModal.propTypes = {
  error: PropTypes.object,
}
